// AS-2a — the Material Library's selection, carried into an Assembly.
// Two actions for select mode: make a new assembly from what is selected, or
// add the selection to one that already exists.
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layers, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AssemblyBulkActions({ clipIds, onDone }) {
  const navigate = useNavigate();
  const [making, setMaking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [systems, setSystems] = useState([]);
  const [title, setTitle] = useState('');
  const [system, setSystem] = useState('');
  const [picks, setPicks] = useState([]);
  const [find, setFind] = useState('');
  const [busy, setBusy] = useState(false);
  const count = clipIds.length;

  useEffect(() => {
    if (!making) return;
    axios.get(`${API}/assemblies/systems`).then((res) => {
      const rows = res.data?.systems || [];
      setSystems(rows);
      setSystem((old) => old || rows[0]?.code || '');
    }).catch(() => {});
  }, [making]);

  useEffect(() => {
    if (!adding) return;
    axios.get(`${API}/assemblies`, { params: { pick: 1 } })
      .then((res) => setPicks(res.data?.assemblies || []))
      .catch(() => toast.error('Could not read the assemblies'));
  }, [adding]);

  const make = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/assemblies/from-clips`, {
        title, system, clip_ids: clipIds });
      setMaking(false);
      onDone?.();
      navigate(`/assemblies/${res.data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not make that assembly');
    } finally { setBusy(false); }
  };

  const addTo = useCallback(async (row) => {
    setBusy(true);
    let added = 0;
    let skipped = 0;
    for (const clipId of clipIds) {
      try {
        await axios.post(`${API}/assemblies/${row.id}/products`, { clip_id: clipId });
        added += 1;
      } catch (e) {
        if (e?.response?.status === 409) skipped += 1;
        else toast.error(e?.response?.data?.detail || 'One product did not go on');
      }
    }
    setBusy(false);
    setAdding(false);
    onDone?.();
    toast.success(
      `${added} added${skipped ? ` · ${skipped} already on ${row.code}` : ''}`,
      { action: { label: `Open ${row.code}`, onClick: () => navigate(`/assemblies/${row.id}`) } });
  }, [clipIds, navigate, onDone]);

  const shown = picks.filter((p) => {
    const text = `${p.code} ${p.title} ${p.system_name}`.toLowerCase();
    return text.includes(find.trim().toLowerCase());
  });

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setMaking(true)}
        data-testid="clips-make-assembly">
        <Plus className="w-4 h-4 mr-1" />
        Make an assembly ({count})
      </Button>
      <Button variant="outline" size="sm" onClick={() => setAdding(true)}
        data-testid="clips-add-to-assembly">
        <Layers className="w-4 h-4 mr-1" />
        Add to assembly ({count})
      </Button>

      <Dialog open={making} onOpenChange={setMaking}>
        <DialogContent data-testid="clips-make-assembly-dialog">
          <DialogHeader>
            <DialogTitle>Make an assembly</DialogTitle>
            <DialogDescription>
              {count} selected product{count === 1 ? '' : 's'} go on as its products,
              in the order you picked them.
            </DialogDescription>
          </DialogHeader>
          {systems.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="clips-make-no-systems">
              No systems yet. Create your first in{' '}
              <Link to="/admin?tab=assembly-systems" className="underline">
                Admin → Assemblies
              </Link>
              .
            </div>
          ) : (
            <div className="space-y-3">
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="What is this method called?"
                data-testid="clips-make-assembly-title" />
              <select className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={system} onChange={(e) => setSystem(e.target.value)}
                data-testid="clips-make-assembly-system">
                {systems.map((s) => (
                  <option key={s.code} value={s.code}>{s.code} {s.name}</option>
                ))}
              </select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaking(false)}>Cancel</Button>
            <Button onClick={make} disabled={busy || !title.trim() || !system}
              data-testid="clips-make-assembly-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent data-testid="clips-add-to-assembly-dialog">
          <DialogHeader>
            <DialogTitle>Add to assembly</DialogTitle>
            <DialogDescription>
              {count} selected product{count === 1 ? '' : 's'} go onto the assembly you pick.
            </DialogDescription>
          </DialogHeader>
          <Input value={find} onChange={(e) => setFind(e.target.value)}
            placeholder="Search code, title or system"
            data-testid="clips-add-to-assembly-search" />
          <div className="max-h-64 overflow-auto divide-y">
            {shown.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground"
                data-testid="clips-add-to-assembly-empty">
                No assemblies yet
              </div>
            ) : shown.map((p) => (
              <button key={p.id} type="button" disabled={busy}
                className="w-full text-left py-2 px-1 hover:bg-muted/50 disabled:opacity-50"
                onClick={() => addTo(p)}
                data-testid={`clips-add-to-${p.code}`}>
                <span className="font-mono text-xs mr-2">{p.code}</span>
                <span className="text-sm">{p.title}</span>
                <span className="text-xs text-muted-foreground ml-2">
                  {p.system_name}{p.status === 'draft' ? ' · draft' : ''}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AssemblyBulkActions;
